<template>
  <ClientOnly>
    <ApiError v-if="apiError" />
    <div v-else>
      <div
        v-if="type === TomasWidgetType.widget"
        class="tomas-booking tomas-booking-widget"
      >
        <div v-if="!hideBookingNotice" class="mb-size-4 text-lg tablet:text-xl">
          <h4 class="font-bold">
            {{
              $texts(
                'tomasBookingNoticeTitle',
                'Note for groups of 7 people or more:',
              )
            }}
          </h4>
          <div class="booking-notice-text" v-html="tomasBookingNoticeText" />
        </div>
        <tws-experience-widget v-pre ref="el" />
      </div>
      <div
        v-if="type === TomasWidgetType.shop"
        class="tomas-booking tomas-booking-shop container"
      >
        <div id="twebshop-experience-shop" ref="el" />
      </div>
    </div>
  </ClientOnly>
</template>

<script lang="ts" setup>
import { DomainId } from '#graphql-operations'
import { PRECONNECT_TOMAS } from '~/helpers/head'
import { TomasWidgetType } from '~/layers/tomasBooking/components/TomasWidget/types'
const route = useRoute()
const { $texts } = useEasyTexts()

const props = withDefaults(
  defineProps<{
    tomasId?: string
    widgetId?: string
    maxServiceCount?: string
    type?: TomasWidgetType
    hideBookingNotice?: boolean
  }>(),
  {
    tomasId: '',
    widgetId: '',
    maxServiceCount: '',
    type: TomasWidgetType.widget,
    hideBookingNotice: false,
  },
)

const apiError = ref(false)
const isInitialized = ref(false)
const { activeDomain } = useSiteContext()

const el = ref<HTMLDivElement>()

useHead({
  link: [
    {
      rel: 'preconnect',
      href: PRECONNECT_TOMAS.href,
    },
  ],
})

function getWidgetId(): string {
  if (props.widgetId) {
    return props.widgetId
  }
  if (activeDomain === DomainId.eyz) {
    return 'eyz_tds_shop'
  } else if (activeDomain === DomainId.rapperswil_zuerichsee_ch) {
    return 'rapperswil_tds_shop'
  }
  return 'zurich_tds'
}

function getAffiliateRole(): string | null {
  if (route.query.partner) {
    const role = route.query.partner.toString()
    if (role.startsWith('TDS')) {
      return role
    }
  }
  return null
}

const url = computed(() => {
  if (props.type === TomasWidgetType.shop) {
    return `https://twebshop.tomas-travel.com/app/experience/shop/app.js`
  }
  return `https://twebshop.tomas-travel.com/app/experience/widget/app.js`
})

const { load: loadScript, remove } = useScript(
  {
    src: url.value,
  },
  {
    trigger: 'manual',
  },
)

async function init() {
  if (!el.value || isInitialized.value) {
    return
  }

  isInitialized.value = true

  if (props.tomasId) {
    el.value.dataset.id = props.tomasId
  }

  const maxServiceCount = props.maxServiceCount
    ? parseInt(props.maxServiceCount)
    : 6

  window.TWEBSHOP = {
    config: getWidgetId(),
    theme: {
      sticky: {
        offset: 80,
        zIndex: 1,
      },
    },
    language: useCurrentLanguage().value,
    maxServiceCount,
    conversion: {
      ga4: true,
      eec: true,
    },
    role: getAffiliateRole() || undefined,
  }

  await loadScript()
}

// Try to initialise the shidget on mounted...
onMounted(init)
// ... or when the ref element becomes available.
watch(el, init)

onBeforeUnmount(() => {
  try {
    // This removes the script, however...
    remove()
    // ... the script adds some crap all over the window.
    delete window.TWEBSHOP
    delete window.twebshopWebpackJsonp

    // It adds one of its injected scripts as a global property.
    if (window.twebshopApp instanceof HTMLScriptElement) {
      window.twebshopApp.remove()
    }

    // Clean up any other remaining turds.
    document.querySelectorAll('#twebshop').forEach((v) => v.remove())
  } catch {
    // Noop.
  }
})

const tomasBookingNoticeText = computed(() =>
  $texts(
    'tomasBookingNoticeText',
    'Please contact the Tourist Information directly via @email or @phone.',
  )
    .replace('@email', '<a href="mailto:info@zuerich.com">info@zuerich.com</a>')
    .replace('@phone', '<a href="tel:+41442154000">+41 44 215 40 00</a>'),
)
</script>

<style lang="postcss">
.tomas-booking-widget {
  min-height: 156px;
  max-width: 48rem;
}

html {
  #twebshop {
    --tw-button-color: rgb(var(--color-primary));
    --tw-button-text-color: rgb(var(--color-scheme-main));
    --tw-button-price-color: #0078bf;
    --tw-button-price-text-color: rgb(var(--color-white));
    --tw-box-color: #f7f7f7;
    --tw-box-text-color: rgb(var(--color-black));
    --tw-booking-color: rgb(var(--color-highlighted));
    --tw-booking-text-color: rgb(var(--color-scheme-invert));

    @media (prefers-color-scheme: dark) {
      --tw-button-color: rgb(var(--color-primary));
      --tw-button-text-color: rgb(var(--color-scheme-invert));
      --tw-box-color: rgb(var(--color-scheme-main-bis));
    }

    [type='date'],
    [type='email'],
    [type='month'],
    [type='number'],
    [type='password'],
    [type='search'],
    [type='text'],
    [type='time'],
    [type='url'],
    select {
      @apply !bg-background text-scheme-main;
    }
    input::placeholder {
      @apply text-muted;
    }

    .block {
      margin: 0;
    }

    .from-white {
      --tw-gradient-from: rgb(var(--color-background)) !important;
      --tw-gradient-to: rgba(255, 255, 255, 0) !important;
    }

    .t-wrapper-accommodation-widget-host,
    .t-wrapper-accommodation-widget-service,
    .t-wrapper-experience-widget {
      max-width: initial !important;
    }

    .t-wrapper-experience-shop {
      @apply !bg-background;

      & .text-tw-boxtext {
        @apply !text-scheme-invert;
      }
    }

    .t-shoppingcart-modal {
      & .bg-tw-box.text-tw-boxtext {
        @apply !text-white;
        background-color: #1d6bb1 !important;
      }
    }

    .t-wrapper {
      @apply !text-scheme-invert;
    }

    .t-card {
      @apply !text-scheme-invert;
    }

    .rounded,
    .rounded-md,
    .rounded-l,
    .rounded-b,
    .rounded-r {
      border-radius: 0 !important;
    }

    .text-white {
      @apply !text-scheme-main;
    }

    .bg-white {
      @apply !bg-background;
    }
    .bg-gray-200 {
      @apply !bg-scheme-main-bis;
    }
    .bg-gray-500 {
      @apply !bg-scheme-main-ter;
    }
    .bg-gray-700,
    .bg-gray-800 {
      @apply !bg-scheme-invert-bis;
    }
    .border-gray-300 {
      @apply !border-border;
    }
    .c-calendar__dropdown {
      .bg-forest-green-100 {
        @apply !bg-disabled;
      }
      .bg-forest-green-700,
      .bg-forest-green-600,
      .bg-forest-green-500,
      .bg-forest-green-400 {
        @apply !bg-primary;
      }
      .hover\:bg-forest-green-100:hover {
        @apply !bg-scheme-main-ter;
      }
      .c-calendar button.bg-forest-green-200 {
        @apply dark:!bg-scheme-main;
      }
    }
    .prose,
    .prose strong,
    .text-gray-900,
    .text-gray-800,
    .text-black {
      @apply !text-scheme-invert;
    }
    .text-gray-900 {
      @apply !bg-background;
    }
    .text-gray-600,
    .text-gray-700 {
      @apply !text-scheme-light;
    }
    .text-gray-500 {
      @apply text-muted;
    }
    .prose {
      a {
        @apply !text-scheme-invert;
      }
    }
    div.rounded.bg-tw-booking.text-tw-bookingtext.mb-4.text-base {
      @apply border border-solid border-border;
    }
    .bg-tw-booking-dark,
    .bg-tw-booking-light {
      @apply !bg-transparent !bg-none;
    }

    .h-1.bg-tw-booking-dark {
      @apply hidden;
    }

    .t-btn--booking:not([disabled]),
    .t-btn:not([disabled]),
    .bg-tw-buttonprice {
      background: none !important;
      @apply !bg-primary !transition-none hover:!bg-primary-light;

      &:hover,
      &:focus {
        .t-btn--booking {
          background: none !important;
          @apply !bg-primary-light;
        }

        .arrow-down {
          @apply !border-t-primary-light;
        }
      }
    }

    .bg-tw-buttonprice {
      .t-btn--booking {
        box-shadow: none !important;
      }
    }

    .border-tw-buttonprice {
      @apply !border-primary;

      .t-btn--booking {
        background: none !important;
        @apply !bg-primary;
      }

      .arrow-down {
        @apply !border-t-scheme-main;
      }
    }

    .c-calendar__dropdown {
      button[type='button'].hover\:bg-gray-100:hover {
        @apply !bg-transparent;
      }

      .grid-cols-7 span [type='button'] {
        @apply !bg-background;
      }

      .grid-cols-7 span [type='button'].bg-gray-100:disabled {
        @apply !bg-transparent !text-scheme-invert !opacity-100;
      }

      .grid-cols-7 span [type='button'].bg-forest-green-200,
      .grid-cols-7 span [type='button'].bg-gray-100 {
        @apply !bg-grey-lightest dark:!bg-black-ter;
      }

      .grid-cols-7 span [type='button'].bg-forest-green-200:hover,
      .grid-cols-7 span [type='button'].bg-gray-100:hover {
        @apply !bg-grey-lighter dark:!bg-grey-darker;

        &:disabled {
          @apply !bg-transparent;
        }
      }

      .grid-cols-7 span [type='button'].bg-forest-green-500 {
        @apply !bg-primary !text-white;
      }
    }

    .c-calendar input {
      @apply !border !border-solid !border-grey-lighter dark:!border-grey-darker;
    }
  }
}

.booking-notice-text {
  a {
    @apply text-primary hover:text-primary-light;
  }
}
</style>
